import { BA, NumberUtils, Prescription, TE } from '@ca/common'

const radiansToDegrees = (rads: number) => {
  return (rads * 180) / Math.PI
}

export const cartesianCoordinatesToPolarRadius = (
  xVal: number | undefined,
  yVal: number | undefined
) => {
  if (xVal == null || yVal == null) return undefined

  const result = Math.sqrt(Math.pow(xVal, 2) + Math.pow(yVal, 2))

  return NumberUtils.round.decimal(result, 2)
}

const lensPolarAnglesMap = {
  r: {
    in: {
      up: (v: number) => v,
      down: (v: number) => 360 - v,
    },
    out: {
      up: (v: number) => 180 - v,
      down: (v: number) => 180 + v,
    },
  },
  l: {
    in: {
      up: (v: number) => 180 - v,
      down: (v: number) => 180 + v,
    },
    out: {
      up: (v: number) => v,
      down: (v: number) => 360 - v,
    },
  },
} as const

export const cartesianCoordinatesToPolarAngle = ({
  xVal,
  xDir,
  yVal,
  yDir,
  lens,
}: {
  xVal: number | undefined
  xDir: TE
  yVal: number | undefined
  yDir: BA
  lens: 'r' | 'l'
}) => {
  if (xVal == null || yVal == null || xDir == null || yDir == null) {
    return undefined
  }

  if (xVal == 0 && yVal == 0) {
    return 0
  }

  const degreesRounded = NumberUtils.round.decimal(
    radiansToDegrees(Math.atan(yVal / xVal)),
    2
  )

  const angle = lensPolarAnglesMap[lens][xDir][yDir](degreesRounded)

  return angle % 360
}

const getNumber = (value?: string | number | null) => {
  if (!value) {
    return undefined
  }
  if (typeof value === 'number') {
    return value
  }
  const num = parseFloat(value)

  if (Number.isFinite(num)) {
    return num
  }
  return undefined
}

export const getBase1FromValues = (
  values: Partial<Prescription>,
  lens: 'r' | 'l'
) => {
  const xVal = getNumber(values[`${lens}_prism1`])
  const yVal = getNumber(values[`${lens}_prism2`])
  const xDir = values[`${lens === 'l' ? 'left' : 'right'}_na_te`] as TE
  const yDir = values[`${lens === 'l' ? 'left' : 'right'}_ha_ba`] as BA

  return cartesianCoordinatesToPolarAngle({
    xVal,
    xDir,
    yVal,
    yDir,
    lens,
  })
}

export const getBase2FromValues = (
  values: Partial<Prescription>,
  lens: 'r' | 'l'
) => {
  const xVal = getNumber(values[`${lens}_prism1`])
  const yVal = getNumber(values[`${lens}_prism2`])
  return cartesianCoordinatesToPolarRadius(xVal, yVal)
}

export const getPrismLensPd = (
  values: Partial<Prescription>,
  lens: 'r' | 'l'
) => {
  const pd = getNumber(values[`${lens}_pd`])
  if (pd == null) {
    return 0
  }
  const xDir = values[`${lens === 'l' ? 'left' : 'right'}_na_te`] as TE
  if (!xDir) {
    return pd
  }
  const prism = getNumber(values[`${lens}_prism1`])
  if (prism == null || prism === 0) {
    return pd
  }
  const dir = xDir === 'in' ? 1 : -1
  const adjusted = pd + prism * 0.25 * dir
  return NumberUtils.round.decimal(adjusted, 2)
}

export const getPrismPd = (values: Partial<Prescription>) => {
  const r_pd = getPrismLensPd(values, 'r')
  const l_pd = getPrismLensPd(values, 'l')
  return {
    r_pd,
    l_pd,
  }
}
